// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, type ReactNode } from 'react';

import { type WorkOrderItem, useEvolvePost, type WorkOrder } from '@evolvemep/api-ts';

import { isNotNil } from 'helpers/isNotNil';
import { AddItemsFromCatalogModal } from 'modules/Shop/CatalogSetup/AddItemsFromCatalogModal';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import type {
  PartToAddWithDecisions,
  PartToAdd,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/CatalogPartAdder';
import { DeleteWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/DeleteWorkOrderItem';

export type AddWorkOrderItemsFromCatalogModalProps = {
  opened: boolean;
  onClose: () => void;
  workOrder: WorkOrder;
  parentWorkOrderItem?: WorkOrderItem;
  onItemsChanged: () => void;
  /** Render the existing item list */
  children: (opts: { onDeleteItem: (workOrderItem: WorkOrderItem) => void }) => ReactNode;
};

/**
 * Used to add items onto a Work Order (or an item BOM) from the Catalog.
 *
 * Expected to be wrapped by a parent that renders the existing items.
 * */
export const AddWorkOrderItemsFromCatalogModal = ({
  onItemsChanged,
  workOrder,
  parentWorkOrderItem,
  children,
  ...props
}: AddWorkOrderItemsFromCatalogModalProps) => {
  const [deletingItem, setDeletingItem] = useState<WorkOrderItem | null>(null);

  const { apiCall: addCatalogItemToWorkOrder } = useEvolvePost('shop/workOrder/item/fromPart');

  const onAddParts = async (parts: (PartToAdd | PartToAddWithDecisions)[]) => {
    await Promise.all(
      parts.map(async ({ part: { partId }, ...item }) =>
        addCatalogItemToWorkOrder({
          ...item,
          partId,
          workOrderId: workOrder.workOrderId,
          parentWorkOrderItemId: parentWorkOrderItem?.workOrderItemId,
        }),
      ),
    ).then(onItemsChanged);
  };

  return (
    <>
      <AddItemsFromCatalogModal
        {...props}
        title={isNotNil(parentWorkOrderItem) ? parentWorkOrderItem.name : workOrder.name}
        subtitle={isNotNil(parentWorkOrderItem) ? 'Add to BOM' : 'Add items'}
        miniHeader={isNotNil(parentWorkOrderItem) ? getDisplayNameWO(workOrder) : workOrder.identifier}
        onAddParts={onAddParts}
        disableAssemblies={isNotNil(parentWorkOrderItem)}
      >
        {children({ onDeleteItem: setDeletingItem })}
      </AddItemsFromCatalogModal>

      <DeleteWorkOrderItem
        workOrderItem={deletingItem}
        onClose={() => setDeletingItem(null)}
        onDeleted={onItemsChanged}
      />
    </>
  );
};
